import getSportEmoji from '../../utils/sportEmoji';

/**
 * Format a date string to a readable format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string (e.g., "Jan 26, 2026")
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Determine tournament status based on dates
 * @param {string} startDate - Tournament start date
 * @param {string} endDate - Tournament end date
 * @param {string} registrationDeadline - Registration deadline date
 * @returns {string} Status string (e.g., "Completed", "Ongoing", "Registration Open", "Upcoming")
 */
export const getTournamentStatus = (startDate, endDate, registrationDeadline) => {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  const regDeadline = registrationDeadline ? new Date(registrationDeadline) : null;

  if (now > end) return 'Completed';
  if (now >= start && now <= end) return 'Ongoing';
  if (regDeadline && now <= regDeadline) return 'Registration Open';
  return 'Upcoming';
};

/**
 * Transform API tournament data to match UI expectations
 * @param {Object} apiTournament - Raw tournament data from API
 * @returns {Object} Transformed tournament object for UI
 */
export const transformTournamentData = (apiTournament) => {
  // Handle cases where related data might not be populated
  const sportName = apiTournament.sportType?.sportName || 'General';
  const facilityName = apiTournament.facility?.facilityName || '';
  // null, never a stand-in name. A tournament with no organizer is a broken
  // record, not an anonymous one: organizer_user_id is what assertIsOrganizer
  // compares against, so NULL there means every caller fails the check and
  // nobody — not the creator, not an admin — can manage the tournament ever
  // again. Rendering "Tournament Organizer" made that look like a normal card
  // and hid the failure until someone found themselves locked out. Callers
  // decide how to present the absence; they must not substitute a name.
  const organizerName = apiTournament.organizerUser?.firstName
    ? `${apiTournament.organizerUser.firstName} ${apiTournament.organizerUser.lastName || ''}`.trim()
    : null;

  // Use API status field if available, otherwise derive from dates
  const status = apiTournament.status
    ? apiTournament.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    : getTournamentStatus(apiTournament.startDate, apiTournament.endDate, apiTournament.registrationDeadline);

  return {
    id: apiTournament.id,
    name: apiTournament.tournamentName || 'Untitled Tournament',
    sport: sportName,
    status: status,
    location: facilityName,
    startDate: formatDate(apiTournament.startDate),
    startDateRaw: apiTournament.startDate,
    endDate: formatDate(apiTournament.endDate),
    description: apiTournament.description || 'No description provided',
    emoji: getSportEmoji(sportName),
    registrationDeadline: formatDate(apiTournament.registrationDeadline),
    registrationDeadlineRaw: apiTournament.registrationDeadline,
    organizerName: organizerName,
    ownerType: apiTournament.ownerType || null,
    ownerId: apiTournament.ownerId || null,
    ownerName: apiTournament.ownerName || null,
    difficultyLevel: apiTournament.difficultyLevel,
    matchConfiguration: apiTournament.matchConfiguration,
    tournamentImage: apiTournament.tournamentImage,
    prizeDetail: apiTournament.prizeDetail,
    // Default values for missing fields
    participants: 0,
    maxParticipants: apiTournament.maxParticipants || 16,
    prize: apiTournament.prizeDetail?.description || 'TBA',
    /* THE TAG FIRST. `registrationFee` is the legacy column and nothing
     * charges against it; a tournament tagged at $25 with a null column read
     * "Free" on every card while checkout asked for $25. */
    registrationFee: (apiTournament.price?.amount ?? apiTournament.registrationFee)
      ? `$${apiTournament.price?.amount ?? apiTournament.registrationFee}`
      : 'Free',
    organizerRating: 4.5,
    difficulty: apiTournament.difficultyLevel?.replace('_', ' ') || 'All Levels',
    myRank: null,
    totalMatches: null,
    wonMatches: null,
    lostMatches: null,
    remainingMatches: null,
    nextMatch: 'TBA',
    deadline: formatDate(apiTournament.registrationDeadline)
  };
};
